import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, DatePicker, Popover, Row, Select, Space } from "antd";
import {
  BarChartOutlined,
  EuroOutlined,
  FileDoneOutlined,
  FilterOutlined,
  LeftOutlined,
  RightOutlined,
  ShoppingOutlined,
  SlidersOutlined,
  TagsOutlined,
} from "@ant-design/icons";
import Flex from "components/shared-components/Flex";
import DataDisplayWidget from "components/shared-components/DataDisplayWidget";
import ChartWidget from "components/shared-components/ChartWidget";
import { COLOR_9, COLORS } from "constants/ChartConstant";
import { getSalesDashboardData } from "../../../../functions/reports";
import { formatPrice, formatWeight } from "../../../../functions/formatters";
import { CURRENCY } from "../../../../constants/DeliveryConstants";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import locale from "antd/es/date-picker/locale/nl_NL";
import { DATE_FORMAT_DD_MM_YYYY } from "../../../../constants/DateConstant";
import Loading from "../../../../components/shared-components/Loading";
import { MARKETING_CODES, PRODUCT_COMPANY_GROUPS } from "../../../../constants/ProductConstant";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { useOrders } from "../../../../data/hooks/firestore/useOrders";
import { useProducts } from "../../../../data/hooks/firestore/useProducts";
import { useInvoices } from "../../../../data/hooks/firestore/useInvoices";
import { useCustomers } from "../../../../data/hooks/firestore/useCustomers";
import { useStockReport } from "../../../../data/hooks/firestore/useStockReport";
import TopSellingProducts from "./cards/TopSellingProducts";
import BestOrder from "./cards/BestOrder";
import TopCustomers from "./cards/TopCustomers";
import useReportFilters from "../../../../data/hooks/url-parameters/useReportFilters";
import DeliveryStatistics from "./cards/DeliveryStatistics";
import { StyledSelect } from "../../../../styles/sharedStyles";
import TopComplaintReasons from "./cards/TopComplaintReasons";
import TopPackedProducts from "./cards/TopPackedProducts";
import useIsMobile from "../../../../hooks/useIsMobile";
import styled from "styled-components";

dayjs.extend(advancedFormat);

const { RangePicker } = DatePicker;
const { Option } = Select;

const RevenueChart = ({ data }) => {
  const { t } = useTranslation();
  const [revenueToday, setRevenueToday] = useState(0);
  const chartLabels = useMemo(() => data?.labels || [], [data?.labels]);

  // Custom options for stacked bar with red color for credits
  const customOptions = useMemo(
    () => ({
      colors: [COLORS[0], COLORS[2], COLORS[1], COLORS[5], COLORS[3]], // Red for credits
      chart: {
        stacked: true,
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "55%",
        },
      },
    }),
    [],
  );

  const percentageChange = Math.round(((data.total - data.totalPrevPeriod) / data.totalPrevPeriod) * 100 * 100) / 100;

  useEffect(() => {
    if (!!data && !!data.totalToday) setRevenueToday(data.totalToday);
  }, [data]);

  if (!!data)
    return (
      <Card>
        <Row gutter={16}>
          <Col xs={24} sm={24} md={24} lg={8}>
            <Flex className="h-100" flexDirection="column" justifyContent="between">
              <div>
                <h4 className="mb-0">{t("revenue")}</h4>
                <span className="text-muted">{data.readableDateRange}</span>
              </div>
              <div>
                <div className="mb-4">
                  <div style={{ fontSize: "14px", marginTop: "8px" }}>
                    <div style={{ marginBottom: "4px" }}>
                      <span className="text-muted">{t("total_weight")}: </span>
                      <span>{formatWeight(data.totalWeight)}</span>
                    </div>
                    <div style={{ marginBottom: "8px", paddingBottom: "8px", borderBottom: "1px solid #f0f0f0" }}>
                      <span className="text-muted">{t("average_price")}: </span>
                      <span>{CURRENCY + formatPrice(data.averagePricePerKg) + "/kg"}</span>
                    </div>
                    <div style={{ marginBottom: "4px" }}>
                      <span className="text-muted">{t("gross_revenue")}: </span>
                      <span>{CURRENCY + formatPrice(data.total)}</span>
                    </div>
                    <div style={{ marginBottom: "4px" }}>
                      <span className="text-muted">{t("credits")}: </span>
                      <span style={{ color: "#ff4d4f" }}>-{CURRENCY + formatPrice(data.totalFarmCredits)}</span>
                    </div>
                    <div style={{ marginBottom: "4px" }}>
                      <span className="text-muted">{t("discounts")}: </span>
                      <span>-{CURRENCY + formatPrice(data.totalDiscounts)}</span>
                    </div>
                    <div style={{ marginBottom: "4px" }}>
                      <span className="text-muted">{t("shipping_costs")}: </span>
                      <span>{CURRENCY + formatPrice(data.totalShipping)}</span>
                    </div>
                    <div style={{ marginBottom: "4px" }}>
                      <span className="text-muted">{t("returnable_packaging")}: </span>
                      <span>{CURRENCY + formatPrice(data.totalReturnablePackaging)}</span>
                    </div>
                    <div style={{ marginBottom: "4px" }}>
                      <span className="text-muted">{t("additional_sales")}: </span>
                      <span>{CURRENCY + formatPrice(data.totalAdditionalSales)}</span>
                    </div>
                  </div>
                </div>
                <div className="mb-4" style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px solid #f0f0f0" }}>
                  <h4 className="font-weight-bold text-nowrap mb-0">{CURRENCY + formatPrice(data.totalNetto)}</h4>
                  <span className="text-muted">{t("netto_farm_revenue")}</span>
                </div>
              </div>
            </Flex>
          </Col>
          <Col xs={24} lg={16} style={{ paddingTop: 56 }}>
            <ChartWidget
              card={false}
              series={data.dataPoints}
              xAxis={chartLabels}
              height={450}
              type="bar"
              customOptions={customOptions}
            />
          </Col>
        </Row>
      </Card>
    );
};

const DisplayDataSet = ({ data }) => {
  const { t } = useTranslation();

  if (!!data)
    return (
      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <DataDisplayWidget
            icon={<FileDoneOutlined />}
            value={data.totalOrders}
            title={t("orders")}
            color={COLOR_9}
            vertical={true}
            avatarSize={55}
          />
          <DataDisplayWidget
            icon={<BarChartOutlined />}
            value={CURRENCY + formatPrice(data.totalRevenue / data.totalOrders)}
            title={t("average_order_value")}
            color={COLOR_9}
            vertical={true}
            avatarSize={55}
          />
          <DataDisplayWidget
            icon={<ShoppingOutlined />}
            value={data.totalUnitsSold || 0}
            title={t("total_units_sold")}
            color={COLOR_9}
            vertical={true}
            avatarSize={55}
          />
        </Col>
        <Col xs={24} lg={12}>
          <DataDisplayWidget
            icon={<EuroOutlined />}
            value={CURRENCY + formatPrice(data.totalRevenue)}
            title={t("revenue")}
            color={COLOR_9}
            vertical={true}
            avatarSize={55}
          />
          <DataDisplayWidget
            icon={<SlidersOutlined />}
            value={CURRENCY + formatPrice(data.averagePricePerKg) + "/kg"}
            title={t("average_price")}
            color={COLOR_9}
            vertical={true}
            avatarSize={55}
          />
          <DataDisplayWidget
            icon={<TagsOutlined />}
            value={data.totalUnitsPacked || 0}
            title={t("total_units_packed")}
            color={COLOR_9}
            vertical={true}
            avatarSize={55}
          />
        </Col>
      </Row>
    );
};

const SalesDashboard = () => {
  const { t } = useTranslation();
  const isMobileView = useIsMobile();
  const [filterPopoverVisible, setFilterPopoverVisible] = useState(false);

  // Use the shared report filters hook
  const {
    isCustomRange,
    customDateRange,
    companyFilter,
    marketingCodeFilter,
    activeTimeFilter,
    dateRange,
    dateRangeText,
    onChangeRange,
    onPreviousWeek,
    onNextWeek,
    onCurrentWeek,
    handleCompanyChange,
    handleMarketingCodeChange,
    isFiltersDefault,
    resetFilters,
  } = useReportFilters();

  // Use the useOrders hook with calculated date range
  const { data: orders, loading: ordersLoading, error: ordersError } = useOrders(30, dateRange);

  // Fetch invoices with same date range
  const { data: invoices, loading: invoicesLoading, error: invoicesError } = useInvoices(30, dateRange);

  // Fetch products for revenue breakdown
  const { products, loading: productsLoading } = useProducts();

  // Fetch customers for company filtering
  const { customers, loading: customersLoading } = useCustomers();

  // Fetch stock data for packed units
  const { getAggregatedAddedStock, loading: stockLoading } = useStockReport(dateRange);

  const [salesData, setSalesData] = useState(null);

  const loading = ordersLoading || productsLoading || invoicesLoading || customersLoading || stockLoading;

  // Update sales data whenever orders, products, invoices, customers or company filter changes
  useEffect(() => {
    if (orders && products && invoices && customers && !stockLoading) {
      const start = dateRange[0] ? parseInt(dayjs(dateRange[0]).startOf("day").format("x")) : null;
      const end = dateRange[1] ? parseInt(dayjs(dateRange[1]).endOf("day").format("x")) : null;

      // Pass invoices and customers as parameters
      const dashboardData = getSalesDashboardData(
        orders,
        start,
        end,
        companyFilter,
        products,
        invoices,
        customers,
        marketingCodeFilter,
      );

      // Calculate total units packed from stock data
      const packedProductsData = getAggregatedAddedStock(companyFilter, products);
      const totalUnitsPacked = packedProductsData.reduce((sum, item) => sum + item.totalAddedStock, 0);

      // Add totalUnitsPacked to facts
      if (dashboardData && dashboardData.facts) {
        dashboardData.facts.totalUnitsPacked = totalUnitsPacked;
      }

      setSalesData(dashboardData);
    }
  }, [orders, products, invoices, customers, companyFilter, marketingCodeFilter, dateRange, stockLoading]);

  if (loading) return <Loading />;
  if (ordersError) return <div>Error loading orders: {ordersError.message}</div>;
  if (invoicesError) return <div>Error loading invoices: {invoicesError.message}</div>;

  if (!!salesData)
    return (
      <>
        <Row gutter={16} className={"mb-2"}>
          <Col xs={24} sm={24} md={16} lg={16} xl={15} xxl={14}>
            <Flex alignItems="center" style={{ gap: "16px" }}>
              <h1 style={{ margin: 0 }}>{t("reports")}</h1>
              {!isMobileView && (
                <>
                  <StyledSelect
                    style={{ minWidth: 140, marginLeft: 20 }}
                    $isFiltered={companyFilter !== "All"}
                    value={companyFilter}
                    onChange={handleCompanyChange}
                  >
                    <Option value="All">{t("all_companies")}</Option>
                    {PRODUCT_COMPANY_GROUPS.map((group) => (
                      <Option key={group.value} value={group.value}>
                        {group.label}
                      </Option>
                    ))}
                  </StyledSelect>
                  <StyledSelect
                    style={{ minWidth: 160, marginLeft: 8 }}
                    $isFiltered={marketingCodeFilter !== "All"}
                    value={marketingCodeFilter}
                    onChange={handleMarketingCodeChange}
                  >
                    <Option value="All">{t("all_customer_types")}</Option>
                    {MARKETING_CODES.map((code) => (
                      <Option key={code.value} value={code.value}>
                        {code.label}
                      </Option>
                    ))}
                  </StyledSelect>
                </>
              )}
            </Flex>
          </Col>
          <Col xs={24} sm={24} md={8} lg={8} xl={9} xxl={10}>
            <Flex justifyContent={!isMobileView ? "end" : "start"} alignItems="center">
              {!isMobileView ? (
                <Space>
                  <Button icon={<LeftOutlined />} onClick={onPreviousWeek} disabled={isCustomRange} />
                  <CustomButton
                    onClick={onCurrentWeek}
                    disabled={isCustomRange}
                    type={activeTimeFilter === "current_week" ? "secondary" : "default"}
                  >
                    {t("current_week")}
                  </CustomButton>
                  <Button icon={<RightOutlined />} onClick={onNextWeek} disabled={isCustomRange} />
                  <RangePicker
                    onChange={onChangeRange}
                    locale={locale}
                    format={DATE_FORMAT_DD_MM_YYYY}
                    allowClear
                    value={customDateRange}
                  />
                  {!isFiltersDefault && (
                    <Button onClick={resetFilters} type="primary">
                      {t("reset_filters")}
                    </Button>
                  )}
                </Space>
              ) : (
                <Popover
                  content={
                    <FilterPopoverContent>
                      <div>
                        <FilterLabel>{t("company")}</FilterLabel>
                        <Select
                          value={companyFilter}
                          onChange={handleCompanyChange}
                          style={{ width: "100%" }}
                          size="large"
                        >
                          <Option value="All">{t("all_companies")}</Option>
                          {PRODUCT_COMPANY_GROUPS.map((group) => (
                            <Option key={group.value} value={group.value}>
                              {group.label}
                            </Option>
                          ))}
                        </Select>
                      </div>
                      <div>
                        <FilterLabel>{t("customer_type")}</FilterLabel>
                        <Select
                          value={marketingCodeFilter}
                          onChange={handleMarketingCodeChange}
                          style={{ width: "100%" }}
                          size="large"
                        >
                          <Option value="All">{t("all_customer_types")}</Option>
                          {MARKETING_CODES.map((code) => (
                            <Option key={code.value} value={code.value}>
                              {code.label}
                            </Option>
                          ))}
                        </Select>
                      </div>
                      <div>
                        <FilterLabel>{t("date_range")}</FilterLabel>
                        <Space direction="vertical" style={{ width: "100%" }}>
                          <Button
                            onClick={onCurrentWeek}
                            disabled={isCustomRange}
                            type={activeTimeFilter === "current_week" ? "primary" : "default"}
                            style={{ width: "100%" }}
                            size="large"
                          >
                            {t("current_week")}
                          </Button>
                          <RangePicker
                            onChange={onChangeRange}
                            locale={locale}
                            format={DATE_FORMAT_DD_MM_YYYY}
                            allowClear
                            value={customDateRange}
                            style={{ width: "100%" }}
                            size="large"
                            popupClassName="single-month-range-picker"
                          />
                        </Space>
                      </div>
                      <FilterButtonsContainer>
                        <Button
                          onClick={() => {
                            resetFilters();
                            setFilterPopoverVisible(false);
                          }}
                          style={{ flex: 1 }}
                        >
                          {t("reset")}
                        </Button>
                        <Button type="primary" onClick={() => setFilterPopoverVisible(false)} style={{ flex: 1 }}>
                          {t("apply")}
                        </Button>
                      </FilterButtonsContainer>
                    </FilterPopoverContent>
                  }
                  title={t("filters")}
                  trigger="click"
                  open={filterPopoverVisible}
                  onOpenChange={setFilterPopoverVisible}
                  placement="bottomLeft"
                >
                  <Button
                    icon={<FilterOutlined />}
                    style={{
                      borderColor: !isFiltersDefault ? "#FF4B33" : "",
                      color: !isFiltersDefault ? "#FF4B33" : "",
                      marginTop: 8,
                    }}
                  />
                </Popover>
              )}
            </Flex>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={24} md={24} lg={16} xl={15} xxl={14}>
            <RevenueChart data={salesData.revenue} />
          </Col>
          <Col xs={24} sm={24} md={24} lg={8} xl={9} xxl={10}>
            <DisplayDataSet data={salesData.facts} />
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={24} md={24} lg={12}>
            <TopSellingProducts
              orders={orders}
              dateRangeText={dateRangeText}
              companyFilter={companyFilter}
              marketingCodeFilter={marketingCodeFilter}
              dateRange={dateRange}
            />
          </Col>
          <Col xs={24} sm={24} md={24} lg={12}>
            <BestOrder data={salesData.bestOrders} />
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={24} md={24} lg={12}>
            <TopCustomers data={salesData.topCustomers} />
          </Col>
          <Col xs={24} sm={24} md={24} lg={12}>
            <DeliveryStatistics orders={orders} dateRange={dateRange} dateRangeText={dateRangeText} />
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={24} md={24} lg={12}>
            <TopComplaintReasons
              dateRange={dateRange}
              dateRangeText={dateRangeText}
              companyFilter={companyFilter}
              marketingCodeFilter={marketingCodeFilter}
            />
          </Col>
          <Col xs={24} sm={24} md={24} lg={12}>
            <TopPackedProducts
              dateRange={dateRange}
              dateRangeText={dateRangeText}
              companyFilter={companyFilter}
              marketingCodeFilter={marketingCodeFilter}
            />
          </Col>
        </Row>
      </>
    );
  else return <Loading />;
};

const CustomButton = ({ type, children, ...props }) => {
  const getButtonStyle = () => {
    if (type === "secondary") {
      return {
        backgroundColor: "#f0f0f0",
        borderColor: "#d9d9d9",
        color: "#595959",
      };
    }
    return {};
  };

  return (
    <Button {...props} style={{ ...getButtonStyle(), ...props.style }}>
      {children}
    </Button>
  );
};

const FilterPopoverContent = styled.div`
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FilterLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
`;

const FilterButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  padding-top: 4px;
`;

export default SalesDashboard;
